import type { Guid } from 'guid-typescript';
import { RestManager } from '@/newApi/services/rest/RestManager';
import { getApiHost } from '@/appSettings';
import type { Role } from '@sysarb/sysarb-types/service-base/enums';
import type { CreateUserResponse, ExternalUserRequest, User, UserData, UserLog, UserRole, UserRoleCreate, UserUpdate } from '@sysarb/sysarb-types/services/v1/user';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { useResgate, Collection, CallResult } from '@/login/composables/useResgate';

export class UserService {
    private readonly userService = new RestManager(`https://${getApiHost()}/base-registry-service`);

    public abort(): void {
        this.userService.abort();
    }

    public async getByEmployeeId(resgate: ReturnType<typeof useResgate>, employeeId: Guid | string): Promise<User | null> {
        if (employeeId == null || employeeId === '') return Promise.resolve(null);
        const { instanceTopic } = useLoginStore();
        return resgate.model<User>(`v1.${instanceTopic}.userbyemployee.${employeeId}`).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    public async create(resgate: ReturnType<typeof useResgate>, userData: UserData): Promise<User | null> {
        const { instanceTopic } = useLoginStore();
        return resgate.call<User>(`v1.${instanceTopic}.users`, 'create', userData).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    public async createMany(resgate: ReturnType<typeof useResgate>, employeeId: (Guid | string)[]): Promise<CreateUserResponse | null> {
        const { instanceTopic } = useLoginStore();
        return resgate.call<CreateUserResponse>(`v1.${instanceTopic}.users`, 'createmany', employeeId).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    public async validateUsersForCreation(resgate: ReturnType<typeof useResgate>, employeeId: (Guid | string)[]): Promise<CreateUserResponse | null> {
        const { instanceTopic } = useLoginStore();
        return resgate.call<CreateUserResponse>(`v1.${instanceTopic}.users`, 'validateforcreate', employeeId).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    public async sendWelcomeMail(resgate: ReturnType<typeof useResgate>, userId: Guid | string): Promise<void | null> {
        const { instanceTopic } = useLoginStore();
        return resgate.call<void>(`v1.${instanceTopic}.user.${userId}`, 'sendwelcomemail', {}).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    public async sendForgotPassword(resgate: ReturnType<typeof useResgate>, userId: Guid | string): Promise<void | null> {
        const { instanceTopic } = useLoginStore();
        return resgate.call<void>(`v1.${instanceTopic}.user.${userId}`, 'sendforgotpasswordmail', {}).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    public async forcePasswordChange(resgate: ReturnType<typeof useResgate>, userId: Guid | string): Promise<boolean | null> {
        const { instanceTopic } = useLoginStore();
        return resgate.call<void>(`v1.${instanceTopic}.user.${userId}`, 'forcenewpassword', {}).then(res => {
            if (res.success !== true) {
                return false;
            }
            return true;
        });
    }

    public async getByInstanceId(resgate: ReturnType<typeof useResgate>, instanceUserId: Guid | string): Promise<User | null> {
        const { instanceTopic } = useLoginStore();
        return resgate.model<User>(`v1.${instanceTopic}.userinstance.${instanceUserId}`).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    public async getExternalUsers(): Promise<({ InstanceId: string, Username: string, Email: string, SocialSecurityNumber: string, Employee_ID: string, LoginServiceUser: string }[]) | null> {
        const { instance } = useLoginStore();
        return this.userService.post(null, `v1/${instance}/get-external`);
    }

    public async update(resgate: ReturnType<typeof useResgate>, userId: Guid | string, updateData: UserUpdate): Promise<CallResult<unknown>> {
        const { instanceTopic } = useLoginStore();
        return resgate.call(`v1.${instanceTopic}.userinstance.${userId}`, 'update', updateData);
    }

    public async delete(resgate: ReturnType<typeof useResgate>, userId: Guid | string): Promise<unknown> {
        const { instanceTopic } = useLoginStore();
        return resgate.call(`v1.${instanceTopic}.userinstance.${userId}`, 'delete', {});
    }

    public async setLanguage(resgate: ReturnType<typeof useResgate>, userId: Guid | string, locale: string): Promise<unknown> {
        const { instanceTopic } = useLoginStore();
        return resgate.call(`v1.${instanceTopic}.user.${userId}`, 'setlanguage', { language: locale });
    }

    public async getLogs(resgate: ReturnType<typeof useResgate>, userId: Guid | string): Promise<Collection<UserLog> | null> {
        const { instanceTopic } = useLoginStore();
        return resgate.collection<UserLog>(`v1.${instanceTopic}.user.${userId}.logs`).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    public async getRoles(resgate: ReturnType<typeof useResgate>, userId: Guid | string): Promise<Collection<UserRole> | null> {
        const { instanceTopic } = useLoginStore();
        return resgate.collection<UserRole>(`v1.${instanceTopic}.user.${userId}.roles`).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    public async createRole(resgate: ReturnType<typeof useResgate>, userId: Guid | string, role: Role, organisationNodeId: Guid | string, unionId?: string | null): Promise<UserRole | null> {
        const data: UserRoleCreate = {
            role,
            organisationNodeId: typeof organisationNodeId === 'string' ? organisationNodeId : organisationNodeId.toString(),
            unionId: unionId ?? undefined
        };
        const { instanceTopic } = useLoginStore();
        return resgate.call<UserRole>(`v1.${instanceTopic}.user.${userId}.roles`, 'create', data).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    public async deleteRole(resgate: ReturnType<typeof useResgate>, userId: Guid | string, roleId: Guid | string): Promise<UserRole | null> {
        const { instanceTopic } = useLoginStore();
        return resgate.call<UserRole>(`v1.${instanceTopic}.user.${userId}.role.${roleId}`, 'delete', {}).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    public async sendVerifyEmail(resgate: ReturnType<typeof useResgate>): Promise<boolean | null> {
        const { userId, instanceTopic } = useLoginStore();
        return resgate.call<boolean>(`v1.${instanceTopic}.user.${userId}`, 'sendverifymail', {}).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    public async verifyEmail(resgate: ReturnType<typeof useResgate>, userId: string, verificationCode: string): Promise<boolean> {
        try {
            await this.userService.post({ userId, verificationCode }, 'v1/system/myuser/verifyemail');
            return true;
        } catch (e: unknown) {
            console.error('Error verifying email', e);
            return false;
        }
    }

    public async sendForgotPasswordMail(resgate: ReturnType<typeof useResgate>, username: string): Promise<void> {
        try {
            await this.userService.post({ username }, 'v1/system/myuser/sendforgotpasswordmail');
        } catch (e: unknown) {
            console.error('Error verifying email', e);
        }
    }

    // Can be called by the user itself or by an administrator on the instance.
    public async setPassword(resgate: ReturnType<typeof useResgate>, userId: string, password: string, validEmail?: boolean): Promise<CallResult<{ error: boolean, errorCode: number }>> {
        const { instanceTopic } = useLoginStore();
        const result = await resgate.call<{ error: boolean, errorCode: number }>(`v1.${instanceTopic}.user.${userId}`, 'setpassword', {
            userId,
            password,
            validEmail
        });
        return result;
    }

    // Can be called by anyone.
    public async setForgottenPassword(resgate: ReturnType<typeof useResgate>, userId: string, password: string, verificationCode: string, verifyEmail?: boolean): Promise<void> {
        const body = {
            id: userId,
            password,
            verificationCode,
            validEmail: verifyEmail
        };

        try {
            await this.userService.post(body, 'v1/system/myuser/resetpassword');
        } catch (e: unknown) {
            console.error('Error verifying email', e);
        }
    }

    public async createExternalUser(newUser: ExternalUserRequest): Promise<{ userId?: string, error?: string } | null> {
        const { instance } = useLoginStore();
        return this.userService.post(newUser, `v1/${instance}/create-external`);
    }

    public async usernameExists(resgate: ReturnType<typeof useResgate>, username: string): Promise<boolean | null> {
        const { instanceTopic } = useLoginStore();
        return resgate.call<{ exists: boolean }>(`v1.${instanceTopic}.users`, 'usernameexists', { username }).then(res => {
            if (res?.success === false) return null;
            return res.data?.exists ?? null;
        });
    }

    public async socialSecurityNumberExists(resgate: ReturnType<typeof useResgate>, socialSecurityNumber: string): Promise<boolean | null> {
        const { instanceTopic } = useLoginStore();
        return resgate.call<{ exists: boolean }>(`v1.${instanceTopic}.users`, 'socialnumberexistslogin', { socialSecurityNumber }).then(res => {
            if (res?.success === false) return null;
            return res.data?.exists ?? null;
        });
    }
}
